import { parseGames, type ParseTree } from "@mliebelt/pgn-parser";
import { findProtectedSpanEnd, maskPgnMovetextCustomSlashNags } from "~/utils/pgnCustomNagMask";
import { formatImportError } from "./importFeedback";
import { freezePgnFacts, isPgnSyntaxError, type Immutable } from "./pgnPreparationFacts";

export type PgnImportParseError = {
  code: "source-too-large" | "variation-depth-limit" | "too-many-games" | "move-limit" | "invalid-pgn";
  message: string;
  gameIndex?: number;
  line?: number;
  column?: number;
};

export function checkPgnImportSourceBytes(bytes: number): PgnImportParseError | null {
  if (bytes > 1024 * 1024) {
    return { code: "source-too-large", message: formatImportError("source-too-large") };
  }
  return null;
}

function checkPgnImportSource(source: string): PgnImportParseError | null {
  // Blob encodes strings as UTF-8 in both browsers and the server runtime.
  const sizeError = checkPgnImportSourceBytes(new Blob([source]).size);
  if (sizeError) return sizeError;
  let depth = 0;
  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];
    if (character === "{" || character === ";" || character === '"') {
      index = findProtectedSpanEnd(source, index) - 1;
    } else if (character === "(") {
      depth += 1;
      if (depth > 32) {
        return { code: "variation-depth-limit", message: formatImportError("variation-depth-limit") };
      }
    } else if (character === ")") {
      depth -= 1;
    }
  }
  return null;
}

function checkPgnImportGames(games: readonly BoundedPgnGame[]): PgnImportParseError | null {
  if (games.length > 100) {
    return { code: "too-many-games", message: formatImportError("too-many-games") };
  }
  if (games.reduce((total, game) => total + game.suppliedNodeCount, 0) > 20000) {
    return { code: "move-limit", message: formatImportError("move-limit") };
  }
  return null;
}

export type BoundedPgnGame = Immutable<ParseTree> & { readonly originalIndex: number; readonly suppliedNodeCount: number };

export type BoundedPgnBatch = { readonly originalCount: number; readonly games: readonly BoundedPgnGame[]; readonly replacements: ReadonlyMap<string, string> };

type BoundedPgnResult =
  | ({ ok: true } & BoundedPgnBatch)
  | { ok: false; error: PgnImportParseError };

/** Shared browser/server preflight: source guards, one existing-parser pass, then parsed bounds. */
export function parsePgnWithinImportBudgets(source: string): BoundedPgnResult {
  const sourceError = checkPgnImportSource(source);
  if (sourceError) return { ok: false, error: sourceError };
  // Check original bytes before removing the optional UTF-8 BOM for parsing.
  const normalizedSource = source.startsWith("\uFEFF") ? source.slice(1) : source;
  const masked = maskPgnMovetextCustomSlashNags([normalizedSource]);
  const parseSource = masked.sources[0] ?? normalizedSource;
  try {
    const games = parseGames(parseSource, { startRule: "games", captureSource: true, captureHeaderText: true }).map((game, originalIndex) => {
      let suppliedNodeCount = 0;
      const pending = [game.moves];
      while (pending.length > 0) {
        for (const move of pending.pop()!) {
          suppliedNodeCount += 1;
          for (const variation of move.variations) pending.push(variation);
        }
      }
      return { ...game, originalIndex, suppliedNodeCount };
    });
    if (games.length === 0) {
      return { ok: false, error: { code: "invalid-pgn", message: formatImportError("invalid-pgn") } };
    }
    const gamesError = checkPgnImportGames(games);
    return gamesError
      ? { ok: false, error: gamesError }
      : Object.freeze({ ok: true, originalCount: games.length, games: freezePgnFacts(games), replacements: masked.replacements });
  } catch (error) {
    if (!isPgnSyntaxError(error)) throw error;
    if (typeof error === "object" && error !== null && "location" in error &&
      typeof error.location === "object" && error.location !== null && "start" in error.location &&
      typeof error.location.start === "object" && error.location.start !== null &&
      "line" in error.location.start && "column" in error.location.start) {
      const { line, column } = error.location.start;
      if (typeof line === "number" && typeof column === "number" &&
        Number.isInteger(line) && line > 0 && Number.isInteger(column) && column > 0) {
        if (parseSource !== normalizedSource) {
          // NAG placeholders preserve lines, but their lengths can shift columns.
          return { ok: false, error: { code: "invalid-pgn", message: formatImportError("invalid-pgn", { line }), line } };
        }
        return { ok: false, error: {
          code: "invalid-pgn", message: formatImportError("invalid-pgn", { line, column }), line, column,
        } };
      }
    }
    return { ok: false, error: { code: "invalid-pgn", message: formatImportError("invalid-pgn") } };
  }
}
